import { track } from '../../../services/analytics/analytics'
import { RechargeType } from '@vacationtracker/shared/types/recharge-type'


export enum RechargeTypeEventName {
  QUIZ_PROMPT_SHOWN = 'RECHARGE_QUIZ_PROMPT_SHOWN',
  QUIZ_PROMPT_HOVERED = 'RECHARGE_QUIZ_PROMPT_HOVERED',
  DISCOVER_TYPE_CLICKED = 'RECHARGE_DISCOVER_TYPE_CLICKED',
  QUIZ_STARTED = 'RECHARGE_QUIZ_STARTED',
  QUIZ_QUESTION_ANSWERED = 'RECHARGE_QUIZ_QUESTION_ANSWERED',
  QUIZ_COMPLETED = 'RECHARGE_QUIZ_COMPLETED',
  QUIZ_MODAL_DISMISSED = 'RECHARGE_QUIZ_MODAL_DISMISSED',
  BADGE_HOVERED = 'RECHARGE_BADGE_HOVERED',
  BADGE_CLICKED = 'RECHARGE_BADGE_CLICKED',
  PROFILE_VIEWED = 'RECHARGE_PROFILE_VIEWED',
  QUIZ_RETAKE_CLICKED = 'RECHARGE_QUIZ_RETAKE_CLICKED',
  INTRO_MODAL_DISMISSED = 'RECHARGE_INTRO_MODAL_DISMISSED',
  GAME_UNLOCK_VIEWED = 'RECHARGE_GAME_UNLOCK_VIEWED',
  GAME_STARTED = 'RECHARGE_GAME_STARTED',
  GAME_GUESS_MADE = 'RECHARGE_GAME_GUESS_MADE',
  GAME_ENDED = 'RECHARGE_GAME_ENDED',
  GAME_DISMISSED = 'RECHARGE_GAME_DISMISSED',
  GAME_PLAY_AGAIN_CLICKED = 'RECHARGE_GAME_PLAY_AGAIN_CLICKED',
}

// Session tracking state - manages different session types for recharge type events
class RechargeTypeSessionTracker {
  private sessionId: string | null = null // General session for badge/profile events
  private quizSessionId: string | null = null // Quiz session for quiz flow events
  private gameSessionId: string | null = null // Game session for game flow events

  // Initialize general session (used for badge/profile events)
  private initSession(): string {
    if (!this.sessionId) {
      this.sessionId = `recharge_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }
    return this.sessionId
  }

  // Initialize quiz session (used for quiz flow events)
  private initQuizSession(): string {
    if (!this.quizSessionId) {
      this.quizSessionId = `recharge_quiz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }
    return this.quizSessionId
  }

  // Initialize game session (used for game flow events)
  private initGameSession(): string {
    if (!this.gameSessionId) {
      this.gameSessionId = `recharge_game_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }
    return this.gameSessionId
  }

  private trackEvent(
    eventName: RechargeTypeEventName,
    userId: string,
    companyId: string,
    properties: Record<string, unknown> = {}
  ): void {
    void track(eventName, {
      userId,
      companyId,
      ...properties,
      eventGroup: 'RECHARGE',
    })
  }

  // RECHARGE_QUIZ_PROMPT_SHOWN
  trackQuizPromptShown(properties: {
    userId: string
    companyId: string
    source: string
    leaveTypeId?: string
  }): void {
    const sessionId = this.initQuizSession()
    this.trackEvent(RechargeTypeEventName.QUIZ_PROMPT_SHOWN, properties.userId, properties.companyId, {
      source: properties.source,
      leaveTypeId: properties.leaveTypeId,
      session_id: sessionId,
    })
  }

  // RECHARGE_QUIZ_PROMPT_HOVERED
  trackQuizPromptHovered(properties: {
    userId: string
    companyId: string
    source: string
  }): void {
    const sessionId = this.quizSessionId || this.initQuizSession()
    this.trackEvent(RechargeTypeEventName.QUIZ_PROMPT_HOVERED, properties.userId, properties.companyId, {
      source: properties.source,
      session_id: sessionId,
    })
  }

  // RECHARGE_DISCOVER_TYPE_CLICKED - when user clicks "Discover your Recharge Type" CTA
  trackDiscoverTypeClicked(properties: {
    userId: string
    companyId: string
    source: string
  }): void {
    const sessionId = this.quizSessionId || this.initQuizSession()
    this.trackEvent(RechargeTypeEventName.DISCOVER_TYPE_CLICKED, properties.userId, properties.companyId, {
      source: properties.source,
      session_id: sessionId,
    })
  }

  // RECHARGE_QUIZ_STARTED
  trackQuizStarted(properties: {
    userId: string
    companyId: string
    source: string
  }): void {
    const sessionId = this.quizSessionId || this.initQuizSession()
    this.trackEvent(RechargeTypeEventName.QUIZ_STARTED, properties.userId, properties.companyId, {
      source: properties.source,
      session_id: sessionId,
    })
  }

  // RECHARGE_QUIZ_QUESTION_ANSWERED
  trackQuizQuestionAnswered(properties: {
    userId: string
    companyId: string
    source: string
    questionNumber: 1 | 2 | 3
  }): void {
    const sessionId = this.quizSessionId || this.initQuizSession()
    this.trackEvent(RechargeTypeEventName.QUIZ_QUESTION_ANSWERED, properties.userId, properties.companyId, {
      source: properties.source,
      questionNumber: properties.questionNumber,
      session_id: sessionId,
    })
  }

  // RECHARGE_QUIZ_COMPLETED
  trackQuizCompleted(properties: {
    userId: string
    companyId: string
    source: string
    rechargeType: RechargeType
  }): void {
    const sessionId = this.quizSessionId || this.initQuizSession()
    this.trackEvent(RechargeTypeEventName.QUIZ_COMPLETED, properties.userId, properties.companyId, {
      source: properties.source,
      rechargeType: properties.rechargeType.toLowerCase(),
      session_id: sessionId,
    })
  }

  // RECHARGE_QUIZ_MODAL_DISMISSED
  trackQuizModalDismissed(properties: {
    userId: string
    companyId: string
    source: string
    dismissedAt: 'recharge_q1' | 'recharge_q2' | 'recharge_q3'
    rechargeType?: RechargeType
  }): void {
    const sessionId = this.quizSessionId || this.initQuizSession()
    this.trackEvent(RechargeTypeEventName.QUIZ_MODAL_DISMISSED, properties.userId, properties.companyId, {
      source: properties.source,
      dismissedAt: properties.dismissedAt,
      rechargeType: properties.rechargeType
        ? properties.rechargeType.toLowerCase()
        : undefined,
      session_id: sessionId,
    })
  }

  // RECHARGE_BADGE_HOVERED
  trackBadgeHovered(properties: {
    userId: string
    companyId: string
  }): void {
    const sessionId = this.initSession()
    this.trackEvent(RechargeTypeEventName.BADGE_HOVERED, properties.userId, properties.companyId, {
      session_id: sessionId,
    })
  }

  // RECHARGE_BADGE_CLICKED
  trackBadgeClicked(properties: {
    userId: string
    companyId: string
  }): void {
    const sessionId = this.initSession()
    this.trackEvent(RechargeTypeEventName.BADGE_CLICKED, properties.userId, properties.companyId, {
      session_id: sessionId,
    })
  }

  // RECHARGE_PROFILE_VIEWED
  trackProfileViewed(properties: {
    userId: string
    companyId: string
  }): void {
    const sessionId = this.initSession()
    this.trackEvent(RechargeTypeEventName.PROFILE_VIEWED, properties.userId, properties.companyId, {
      session_id: sessionId,
    })
  }

  // RECHARGE_QUIZ_RETAKE_CLICKED
  trackQuizRetakeClicked(properties: {
    userId: string
    companyId: string
    previousRechargeType: RechargeType
  }): void {
    // Start a new quiz session for retake
    this.quizSessionId = null // Reset to start fresh
    const sessionId = this.initQuizSession()
    this.trackEvent(RechargeTypeEventName.QUIZ_RETAKE_CLICKED, properties.userId, properties.companyId, {
      previousRechargeType: properties.previousRechargeType.toLowerCase(),
      session_id: sessionId,
    })
  }

  // RECHARGE_INTRO_MODAL_DISMISSED - when user closes the intro modal without starting quiz
  trackIntroModalDismissed(properties: {
    userId: string
    companyId: string
    source: string
    isRetakeMode: boolean
  }): void {
    const sessionId = this.quizSessionId || this.initQuizSession()
    this.trackEvent(RechargeTypeEventName.INTRO_MODAL_DISMISSED, properties.userId, properties.companyId, {
      source: properties.source,
      isRetakeMode: properties.isRetakeMode,
      session_id: sessionId,
    })
  }

  // RECHARGE_GAME_UNLOCK_VIEWED
  trackGameUnlockViewed(properties: {
    userId: string
    companyId: string
  }): void {
    const sessionId = this.initSession()
    this.trackEvent(RechargeTypeEventName.GAME_UNLOCK_VIEWED, properties.userId, properties.companyId, {
      session_id: sessionId,
    })
  }

  // RECHARGE_GAME_STARTED
  trackGameStarted(properties: {
    userId: string
    companyId: string
  }): void {
    const sessionId = this.initGameSession()
    this.trackEvent(RechargeTypeEventName.GAME_STARTED, properties.userId, properties.companyId, {
      session_id: sessionId,
    })
  }

  // RECHARGE_GAME_GUESS_MADE
  trackGameGuessMade(properties: {
    userId: string
    companyId: string
    guessIsCorrect: boolean
    currentStreak: number
  }): void {
    const sessionId = this.gameSessionId || this.initGameSession()
    this.trackEvent(RechargeTypeEventName.GAME_GUESS_MADE, properties.userId, properties.companyId, {
      guessIsCorrect: properties.guessIsCorrect,
      currentStreak: properties.currentStreak,
      session_id: sessionId,
    })
  }

  // RECHARGE_GAME_ENDED
  trackGameEnded(properties: {
    userId: string
    companyId: string
    guessIsCorrect: boolean
    currentStreak: number
  }): void {
    const sessionId = this.gameSessionId || this.initGameSession()
    this.trackEvent(RechargeTypeEventName.GAME_ENDED, properties.userId, properties.companyId, {
      guessIsCorrect: properties.guessIsCorrect,
      currentStreak: properties.currentStreak,
      session_id: sessionId,
    })
  }

  // RECHARGE_GAME_DISMISSED
  trackGameDismissed(properties: {
    userId: string
    companyId: string
  }): void {
    const sessionId = this.gameSessionId || this.initGameSession()
    this.trackEvent(RechargeTypeEventName.GAME_DISMISSED, properties.userId, properties.companyId, {
      session_id: sessionId,
    })
  }

  // RECHARGE_GAME_PLAY_AGAIN_CLICKED
  trackGamePlayAgainClicked(properties: {
    userId: string
    companyId: string
  }): void {
    const sessionId = this.gameSessionId || this.initGameSession()
    this.trackEvent(RechargeTypeEventName.GAME_PLAY_AGAIN_CLICKED, properties.userId, properties.companyId, {
      session_id: sessionId,
    })
  }
}

// Export singleton instance
export const rechargeTypeTracking = new RechargeTypeSessionTracker()

